<template>
  <div>
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <span class="label-tag">About</span>
        <h1>The Story Behind the Analysis</h1>
        <p class="page-header-sub">
          A career built at the intersection of football, data, and leadership.
        </p>
      </div>
    </section>

    <!-- Bio -->
    <section class="section section-alt">
      <div class="container">
        <div class="row align-items-start">
          <div class="col-lg-7">
            <div class="divider"></div>
            <p class="bio-lead">
              I'm a football tactics historian, strategist, and data analyst with
              a career that spans the professional game and the corporate boardroom.
            </p>
            <p class="bio-text">
              My journey in football started on the pitch &mdash; as a youth player at
              Crewe Alexandra's academy, representing England and Cheshire at youth
              level. That firsthand experience of the game at its most competitive laid
              the foundation for everything that followed.
            </p>
            <p class="bio-text">
              Over the past three years as a football scout, I've applied analytical
              rigour to player identification and assessment, producing opposition
              reports for clubs across the EFL, National League, and MLS. I hold
              qualifications from the PFSA, FC Barcelona, and the English FA &mdash;
              including completing the Executive Program in The Advanced Strategic
              Management of Professional Football at the Barcelona Innovation Hub,
              where I prepared strategies for top-tier French Ligue 1 clubs.
            </p>
            <p class="bio-text">
              But football is only half the picture. For fifteen years, I led a
              software department within a major corporate organisation, managing
              teams, driving strategy, and delivering complex technology projects.
              That two-decade track record of leadership gives me a perspective that
              most analysts simply don't have: I understand the organisational context
              in which decisions are made.
            </p>
            <p class="bio-text">
              Today, I bring all of it together &mdash; tactical expertise, data
              fluency, leadership experience, and a deep love of the game &mdash; to
              help clubs and organisations make better decisions.
            </p>
          </div>
          <div class="col-lg-4 offset-lg-1">
            <div class="experience-sidebar">
              <div class="experience-item" v-for="item in experience" :key="item.label">
                <div class="experience-value">{{ item.value }}</div>
                <div class="experience-label">{{ item.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Qualifications -->
    <section class="section section-dark">
      <div class="container">
        <div class="text-center mb-5">
          <span class="label-tag">Qualifications</span>
          <h2>Professional Credentials</h2>
        </div>
        <div class="row g-4">
          <div class="col-md-6 col-lg-3" v-for="qual in qualifications" :key="qual.title">
            <div class="card-custom">
              <h3>{{ qual.title }}</h3>
              <ul class="qual-list">
                <li v-for="item in qual.items" :key="item">{{ item }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="section section-alt">
      <div class="container text-center">
        <h2>Want to Know More?</h2>
        <p class="section-subtitle mx-auto mt-3">
          I'm always happy to discuss how my experience can add value to your organisation.
        </p>
        <router-link to="/contact" class="btn-primary-custom">Get in Touch</router-link>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'AboutView',
  data() {
    return {
      experience: [
        { value: '20+', label: 'Years in Leadership' },
        { value: '15', label: 'Years Leading Software' },
        { value: '3', label: 'Years as a Scout' },
        { value: '4+', label: 'Professional Certifications' }
      ],
      qualifications: [
        {
          title: 'PFSA',
          items: [
            'Level 1 Technical Scout',
            'Level 1 Data Analyst',
            'Level 2 Performance Analyst',
            'Level 2 Opposition Analyst'
          ]
        },
        {
          title: 'FC Barcelona',
          items: [
            'Tactical Analysis Diploma',
            'Executive Program: Advanced Strategic Management of Professional Football',
            'Set Piece Analysis',
            'Opposition Analysis',
            'Self Analysis',
            'Coaching Session Design'
          ]
        },
        {
          title: 'English FA',
          items: [
            'Introduction to First Aid in Football',
            'Safeguarding Children',
            'Introduction to Coaching Football'
          ]
        },
        {
          title: 'Player & Coach',
          items: [
            'Crewe Alexandra Academy',
            'England Youth Representative',
            'Cheshire Youth Representative',
            'Youth & Adult Team Management'
          ]
        }
      ]
    }
  }
}
</script>

<style scoped>
.page-header {
  padding: 160px 0 80px;
  background: linear-gradient(160deg, var(--navy-dark) 0%, var(--navy) 100%);
}

.page-header h1 {
  margin-bottom: 1rem;
}

.page-header-sub {
  color: var(--silver);
  font-size: 1.15rem;
  max-width: 520px;
}

.bio-lead {
  font-size: 1.3rem;
  color: var(--white);
  line-height: 1.7;
  margin-bottom: 1.5rem;
  font-weight: 400;
}

.bio-text {
  color: var(--silver-light);
  font-size: 1.05rem;
  margin-bottom: 1.25rem;
  line-height: 1.8;
}

.experience-sidebar {
  background: var(--navy-medium);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 2rem;
  margin-top: 2rem;
}

.experience-item {
  padding: 1.25rem 0;
  border-bottom: 1px solid var(--border-subtle);
}

.experience-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.experience-item:first-child {
  padding-top: 0;
}

.experience-value {
  font-family: var(--font-heading);
  font-size: 2rem;
  font-weight: 700;
  color: var(--accent);
}

.experience-label {
  color: var(--silver);
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 0.25rem;
}

.qual-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.qual-list li {
  color: var(--silver);
  font-size: 0.9rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border-subtle);
  line-height: 1.5;
}

.qual-list li:last-child {
  border-bottom: none;
}
</style>
